import { useFormik } from 'formik';
import type { WarehouseLocation } from 'src/api/warehouses.api';
import { AgButton, AgInput, AgModal, AgModalActions, AgModalContent, AgText } from 'src/components/ui';
import { useCreateWarehouseLocation } from './hooks/useCreateWarehouseLocation';
import { useUpdateWarehouseLocation } from './hooks/useUpdateWarehouseLocation';

type Props = { location: WarehouseLocation | null; tagKeys: string[]; warehouseId: number; onClose: () => void };

export default function EditLocationModal({ location, tagKeys, warehouseId, onClose }: Props) {
  const isEditMode = !!location?.id;

  const { create: createWarehouseLocation, loading: createLoading } = useCreateWarehouseLocation();
  const { update: updateWarehouseLocation, loading: updateLoading } = useUpdateWarehouseLocation();
  const loading = createLoading || updateLoading;

  // Initialize form values
  const initialTags = location?.tags;
  const initialTagValues = tagKeys.reduce<Record<string, WarehouseLocation['tags']>>((acc, key) => {
    const tag =
      initialTags && typeof initialTags === 'object' && !Array.isArray(initialTags) ? initialTags[key] : undefined;
    acc[key] = tag || '';
    return acc;
  }, {});

  const formik = useFormik({
    initialValues: {
      slug: location?.slug || '',
      description: location?.description || '',
      capacity: location?.capacity?.toString() || '',
      tags: initialTagValues,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const { slug, description, capacity, tags } = values;

      const input = {
        slug: slug || null,
        description: description || null,
        capacity: capacity ? parseInt(capacity) : null,
        tags,
      };

      if (isEditMode) {
        await updateWarehouseLocation({ id: location.id, input });
      } else {
        await createWarehouseLocation({ input: { ...input, warehouseId } });
      }

      onClose();
    },
  });

  return (
    <AgModal open onClose={onClose} title={`${isEditMode ? 'Edit' : 'Create'} Location`} size="xs">
      <form onSubmit={formik.handleSubmit}>
        <AgModalContent>
          <AgText size="14" color="secondary" className="mb-3">
            Specify tags and pallet capacity for location you want to add. You can generate as many similar locations as
            needed.
          </AgText>

          <div className="flex flex-col gap-4">
            <AgInput
              name="slug"
              label="Location slug"
              isRequired
              value={formik.values.slug}
              onChange={(value) => formik.setFieldValue('slug', value)}
              onBlur={() => formik.setFieldTouched('slug', true)}
              isInvalid={formik.touched.slug && !!formik.errors.slug}
              errorMessage={formik.touched.slug ? (formik.errors.slug as string) : undefined}
            />
            <AgInput
              name="description"
              label="Description"
              value={formik.values.description}
              onChange={(value) => formik.setFieldValue('description', value)}
              onBlur={() => formik.setFieldTouched('description', true)}
              isInvalid={formik.touched.description && !!formik.errors.description}
              errorMessage={formik.touched.description ? (formik.errors.description as string) : undefined}
            />
            <AgInput
              name="capacity"
              label="Pallet capacity"
              type="number"
              value={formik.values.capacity}
              onChange={(value) => formik.setFieldValue('capacity', value)}
              onBlur={() => formik.setFieldTouched('capacity', true)}
              isInvalid={formik.touched.capacity && !!formik.errors.capacity}
              errorMessage={formik.touched.capacity ? (formik.errors.capacity as string) : undefined}
            />
            {tagKeys.map((tagKey) => (
              <AgInput
                key={tagKey}
                name={`tags.${tagKey}`}
                label={tagKey}
                value={String(formik.values.tags[tagKey] || '')}
                onChange={(value) => formik.setFieldValue(`tags.${tagKey}`, value)}
                onBlur={() => formik.setFieldTouched(`tags.${tagKey}`, true)}
                isInvalid={formik.touched.tags?.[tagKey] && !!formik.errors.tags?.[tagKey]}
                errorMessage={formik.touched.tags?.[tagKey] ? (formik.errors.tags?.[tagKey] as string) : undefined}
              />
            ))}
          </div>
        </AgModalContent>

        <AgModalActions>
          <AgButton type="submit" variant="primary" isDisabled={loading || !formik.isValid}>
            {isEditMode ? 'Update Location' : 'Save Location'}
          </AgButton>
        </AgModalActions>
      </form>
    </AgModal>
  );
}
